import { create } from 'zustand';
import { Account, Bank, Broker, Collateral, Company, Contact, IFormulaToAccount } from '../../sp-types/accountTypes';
import { COLOR_DICTIONARY, materialListToColorDictionary } from '../../sp-types/colors';

// Next set function - just need to write a generic setter
interface accountStore {
  account: Account;
  formulaColorDictionary: Record<string, string>;
  hasAccount: boolean;
  isAccountLoading: boolean;
  setHasAccount: (_value: boolean) => void;
  setIsAccountLoading: (_value: boolean) => void;
  setAccount: (_value: [Account, string]) => void;
  setBankDetails: (_value: Bank) => void;
  setCompanyDetails: (_value: Company) => void;
  setBrokerDetails: (_value: Broker) => void;
  setContactDetails: (_value: Contact) => void;
  setCollateral: (_value: Collateral) => void;
  setId: (_value: string) => void;
  setNewBrokerDetails: (_value: Broker) => void;
  setNewBankDetails: (_value: Bank) => void;
  updateFormulaToAccount: (_updatedFormulaToAccount: IFormulaToAccount) => void;
  setFormulaToAccount: (_updatedFormulaToAccount: IFormulaToAccount[]) => void;
}

export const useAccountStore = create<accountStore>((set) => ({
  account: {
    status: 'inActive',
    creator: '',
    country: '',
    companyDetails: {
      name: '',
      registrationNo: '',
      vat: '',
      tradingAddress: '',
      contact: '',
      signatoryRights: '',
      signatureAuthorized: false,
    },
    collateralDetails: {
      availableCollateral: 1,
      immediateConfirmation: 0,
      newCollateralAmount: 0,
      marginCall: 0,
      allocatedCollateral: 0,
      confirmationDoc: [],
    },
    contactDetails: {
      email: '',
      name: '',
      phoneNumber: '',
    },
    formulasToAccount: [],
    AdjustmentsInfo: { materialAdjustmentsOptions: [], productAdjustmentOptions: [], productPriceIndexes: [] },
    riskPercents: { volatility: 0, riskBackTesting: [] },
  },
  formulaColorDictionary: {},
  hasAccount: false,
  isAccountLoading: false,
  setHasAccount: (value) => set({ hasAccount: value }),
  setIsAccountLoading: (value) => set({ isAccountLoading: value }),
  setAccount: (value) =>
    set({
      account: {
        ...value[0],
        id: value[1],
      },
      formulaColorDictionary: materialListToColorDictionary(
        [...value[0].formulasToAccount.map((item) => item.formulaId), ...(value[0].addMaterials || [])],
        COLOR_DICTIONARY,
      ),
    }),
  setBankDetails: (value) =>
    set((state) => ({
      account: {
        ...state.account,
        bankDetails: {
          ...state.account.bankDetails,
          ...value, // update the bankDetails with the new values provided
        },
      },
    })),
  setCompanyDetails: (value) =>
    set((state) => ({
      account: {
        ...state.account,
        companyDetails: {
          ...state.account.companyDetails,
          ...value, // update the companyDetails with the new values provided
        },
      },
    })),
  setBrokerDetails: (value) =>
    set((state) => ({
      account: {
        ...state.account,
        brokerDetails: {
          ...state.account.brokerDetails,
          ...value, // update the companyDetails with the new values provided
        },
      },
    })),
  setContactDetails: (value) =>
    set((state) => ({
      account: {
        ...state.account,
        contactDetails: {
          ...state.account.contactDetails,
          ...value, // update the companyDetails with the new values provided
        },
      },
    })),
  setCollateral: (value) =>
    set((state) => ({
      account: {
        ...state.account,
        collateral: {
          ...state.account.collateralDetails,
          ...value, // update the companyDetails with the new values provided
        },
      },
    })),
  setId: (value) =>
    set((state) => ({
      account: {
        ...state.account,
        id: value,
      },
    })),
  setNewBrokerDetails: (value) =>
    set((state) => ({
      account: {
        ...state.account,
        brokerDetails: value,
      },
    })),
  setNewBankDetails: (value) =>
    set((state) => ({
      account: {
        ...state.account,
        bankDetails: value,
      },
    })),
  updateFormulaToAccount: (updated: IFormulaToAccount) =>
    set(({ account }) => {
      const exists = account.formulasToAccount.some((e) => e.formulaId === updated.formulaId);
      return {
        account: {
          ...account,
          formulasToAccount: exists
            ? account.formulasToAccount.map((e) => (e.formulaId === updated.formulaId ? updated : e))
            : [...account.formulasToAccount, updated],
        },
      };
    }),
  setFormulaToAccount: (formulasToAccount: IFormulaToAccount[]) =>
    set(({ account }) => ({
      account: {
        ...account,
        formulasToAccount,
      },
      formulaColorDictionary: materialListToColorDictionary(
        [
          ...formulasToAccount.map((item) => item.formulaId),
          ...(account.addMaterials || []),
        ],
        COLOR_DICTIONARY,
      ),
    })),
}));
